import React, { useState, useEffect, useCallback } from 'react';
import { nanoid } from 'nanoid';
import { CompanyData, Product } from '../types';
import { isValidEIN, isValidEmail, formatEIN } from '../utils/validation';
import { validatePhoneNumber } from '../utils/phoneValidation';
import { Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import OrganisationFormFields from './organisation/OrganisationFormFields';

interface CompanyInfoFormProps {
  onSubmit: (data: CompanyData, isFinalSubmit?: boolean) => void;
  initialData?: CompanyData | null;
  disableEin?: boolean,
  onFieldChange?: (data: CompanyData) => void;
  isFinalStep?: boolean;
}

const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({
  onSubmit,
  initialData,
  disableEin,
  onFieldChange,
  isFinalStep = false
}) => {
  const { toast } = useToast();
  const [showTermsModal, setShowTermsModal] = useState(false);

  const [formData, setFormData] = useState<CompanyData>({
    pocName: '',
    pocEmail: '',
    pocPhone: '',
    pocPhoneVerified: false,
    name: '',
    name_dbas: '',
    address: '',
    stateOfIncorporation: '',
    ein: '',
    revenueLast12Months: undefined,
    products: [{ id: nanoid(), name: '', description: '', category: '' }],
    terms: false
  });

  const [formErrors, setFormErrors] = useState({
    pocName: false,
    pocEmail: false,
    pocPhone: false,
    name: false,
    address: false,
    stateOfIncorporation: false,
    ein: false,
    einDuplicate: false,
    products: false,
    terms: false
  });
  const [einError, setEinError] = useState('');
  const [isValidatingEin, setIsValidatingEin] = useState(false);

  // Load saved data from sessionStorage on initial render
  useEffect(() => {
    const savedData = sessionStorage.getItem('figabl_company_data');
    if (savedData || !initialData) {
      try {
        const parsedData = JSON.parse(savedData);
        // Only use sessionStorage data if no initialData is provided
        // Ensure products have IDs
        const productsWithIds = parsedData.products?.map((product: Product) => ({
          ...product,
          id: product.id || nanoid()
        })) || [{ id: nanoid(), name: '', description: '', category: '' }];

        setFormData({
          ...parsedData,
          products: productsWithIds,
          pocPhone: parsedData.pocPhone || '',
          // Preserve phone verification status from saved data
          pocPhoneVerified: parsedData.pocPhoneVerified || false
        });
      } catch (error) {
        console.error('Error parsing sessionStorage company data:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (initialData) {
      const products = Array.isArray(initialData.products)
        ? initialData.products
        : [{ id: nanoid(), name: '', description: '', category: '' }];

      const productsWithIds = products.map(product => ({
        ...product,
        id: product.id || nanoid()
      }));

      setFormData({
        ...initialData,
        products: productsWithIds,
        pocPhone: initialData.pocPhone || '',
        // Preserve phone verification status from initial data
        pocPhoneVerified: initialData.pocPhoneVerified || false
      });
    }
  }, [initialData]);

  // Debounced EIN validation function
  const validateEinRealTime = useCallback(
    async (ein: string) => {
      if (!ein) {
        setEinError('');
        setFormErrors(prev => ({ ...prev, ein: false, einDuplicate: false }));
        return;
      }

      // Check format first
      if (!isValidEIN(ein)) {
        setEinError('Valid EIN format required (XX-XXXXXXX)');
        setFormErrors(prev => ({ ...prev, ein: true, einDuplicate: false }));
        return;
      }

      // If format is valid, check for duplicates via edge function
      setIsValidatingEin(true);
      try {
        const { data, error } = await invokeFunction(supabase, 'validate-ein-duplicate-v2', {
          body: {
            ein,
            organization_id: initialData?.organization_id,
            poc_email: formData.pocEmail
          }
        });

        if (error) {
          console.error('Error calling EIN validation function:', error);
          setEinError('Error validating EIN. Please try again.');
          setFormErrors(prev => ({ ...prev, ein: false, einDuplicate: true }));
          return;
        }

        if (data?.isDuplicate) {
          setEinError('The EIN you entered is invalid or already in use. Please check and try again.');
          setFormErrors(prev => ({ ...prev, ein: false, einDuplicate: true }));
        } else {
          setEinError('');
          setFormErrors(prev => ({ ...prev, ein: false, einDuplicate: false }));
        }
      } catch (error) {
        console.error('Error validating EIN:', error);
        setEinError('Error validating EIN. Please try again.');
        setFormErrors(prev => ({ ...prev, ein: false, einDuplicate: true }));
      } finally {
        setIsValidatingEin(false);
      }
    },
    [formData.pocEmail, initialData?.organization_id]
  );

  // Debounce the EIN validation
  useEffect(() => {
    if (formData.ein) {
      const timeoutId = setTimeout(() => {
        validateEinRealTime(formData.ein);
      }, 500); // 500ms delay

      return () => clearTimeout(timeoutId);
    } else {
      setEinError('');
      setFormErrors(prev => ({ ...prev, ein: false, einDuplicate: false }));
    }
  }, [formData.ein, validateEinRealTime]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    let processedValue: any = value;
    if (name === 'ein') {
      processedValue = formatEIN(value);
    } else if (name === 'revenueLast12Months') {
      // Remove non-numeric characters except decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      processedValue = numericValue ? parseFloat(numericValue) : undefined;
    }

    const updatedFormData = {
      ...formData,
      [name]: processedValue
    };

    setFormData(updatedFormData);

    // Clear errors for the field being changed (except EIN which has real-time validation)
    if (name !== 'ein' && formErrors[name as keyof typeof formErrors]) {
      setFormErrors({ ...formErrors, [name]: false });
    }

    onFieldChange?.(updatedFormData);
  };

  const handlePhoneChange = (phone: string) => {
    const updatedFormData = {
      ...formData,
      pocPhone: phone,
      pocPhoneVerified: false // Reset verification when phone changes
    };

    setFormData(updatedFormData);

    if (formErrors.pocPhone) {
      setFormErrors({ ...formErrors, pocPhone: false });
    }

    onFieldChange?.(updatedFormData);
  };

  const handlePhoneVerificationComplete = (verified: boolean) => {
    const updatedFormData = {
      ...formData,
      pocPhoneVerified: verified
    };

    setFormData(updatedFormData);

    if (verified && formErrors.pocPhone) {
      setFormErrors({ ...formErrors, pocPhone: false });
    }

    onFieldChange?.(updatedFormData);
  };

  const handleProductChange = (id: string, field: keyof Product, value: string) => {
    const updatedProducts = formData.products.map(product =>
      product.id === id ? { ...product, [field]: value } : product
    );

    const updatedFormData = {
      ...formData,
      products: updatedProducts
    };

    setFormData(updatedFormData);

    if (formErrors.products && updatedProducts.some(p => p.name && p.description && p.category)) {
      setFormErrors({ ...formErrors, products: false });
    }

    onFieldChange?.(updatedFormData);
  };

  const addProduct = () => {
    const updatedFormData = {
      ...formData,
      products: [...formData.products, { id: nanoid(), name: '', description: '', category: '' }],
    };

    setFormData(updatedFormData);
    onFieldChange?.(updatedFormData);
  };

  const removeProduct = (id: string) => {
    if (formData.products.length > 1) {
      const updatedFormData = {
        ...formData,
        products: formData.products.filter(product => product.id !== id),
      };

      setFormData(updatedFormData);
      onFieldChange?.(updatedFormData);
    } else {
      toast({
        title: "Cannot Remove",
        description: "You must have at least one product.",
        variant: "destructive",
      });
    }
  };

  const handleTermsCheckedChange = (checked: boolean) => {
    const updatedFormData = {
      ...formData,
      terms: checked
    };
    setFormData(updatedFormData);
    onFieldChange?.(updatedFormData);
  };

  const checkEinDuplicate = async (ein: string): Promise<boolean> => {
    if (!ein || !isValidEIN(ein)) return false;

    try {
      const { data, error } = await invokeFunction(supabase, 'validate-ein-duplicate-v2', {
        body: {
          ein,
          organization_id: initialData?.organization_id,
          poc_email: formData.pocEmail
        }
      });

      if (error) {
        console.error('Error calling EIN validation function:', error);
        return false;
      }

      return data?.isDuplicate || false;
    } catch (error) {
      console.error('Error checking EIN duplicate:', error);
      return false;
    }
  };

  const validateForm = async (): Promise<boolean> => {
    // Validate phone number
    const phoneValidation = formData.pocPhone ? validatePhoneNumber(formData.pocPhone) : { isValid: false };

    // Check for EIN duplicate
    const hasEinDuplicate = await checkEinDuplicate(formData.ein);

    const newErrors = {
      pocName: !formData.pocName.trim(),
      pocEmail: !isValidEmail(formData.pocEmail),
      pocPhone: !formData.pocPhoneVerified, // Require phone verification
      name: !formData.name.trim(),
      address: !formData.address?.trim(),
      stateOfIncorporation: !formData.stateOfIncorporation,
      ein: !isValidEIN(formData.ein),
      einDuplicate: hasEinDuplicate,
      products: false, // Products section disabled
      terms: !formData.terms
    };

    setFormErrors(newErrors);

    if (hasEinDuplicate) {
      setEinError('The EIN you entered is invalid or already in use. Please check and try again.');
    } else {
      setEinError('');
    }

    return !Object.values(newErrors).some(error => error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.terms) {
      toast({
        title: "Terms Required",
        description: "You must accept the Terms of Service to continue",
        variant: "destructive"
      });
      return;
    }

    if (!formData.pocPhoneVerified) {
      toast({
        title: "Phone Verification Required",
        description: "Please verify your phone number before continuing",
        variant: "destructive"
      });
      return;
    }

    const isValid = await validateForm();
    if (isValid) {
      onSubmit(formData);
    } else {
      toast({
        title: "Validation Error",
        description: "Please fix the highlighted fields and try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form-container">
      <OrganisationFormFields
        formData={formData}
        formErrors={formErrors}
        einError={einError}
        isValidatingEin={isValidatingEin}
        handleInputChange={handleInputChange}
        handleProductChange={handleProductChange}
        addProduct={addProduct}
        removeProduct={removeProduct}
        onTermsCheckedChange={handleTermsCheckedChange}
        onPhoneChange={handlePhoneChange}
        onPhoneVerificationComplete={handlePhoneVerificationComplete}
        disableEin={disableEin}
        showTermsModal={showTermsModal}
        setShowTermsModal={setShowTermsModal}
      />

      <div className="flex justify-end">
        <button type="submit" className="form-button flex items-center">
          <Check size={16} className="mr-2" /> Continue
        </button>
      </div>
    </form>
  );
};

export default CompanyInfoForm;
